//region header
//--------------------------------------------------------------------------------------------------
//
//  Description: Display the list of BLPU provenances for the property.
//
//  Copyright:    © 2021 - 2026 Idox Software Limited.
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
//region Version 1.0.0.0
//    001            Sean Flook                  Initial Revision.
//    002   28.06.23 Sean Flook          WI40256 Changed Extent to Provenance where appropriate.
//    003   06.10.23 Sean Flook                  Use colour variables.
//    004   27.10.23 Sean Flook                  Use new dataFormStyle.
//    005   24.11.23 Sean Flook                  Moved Box and Stack to @mui/system and fixed some warnings.
//    006   08.12.23 Sean Flook                  Migrated DataGrid to v6.
//    007   05.01.24 Sean Flook                  Changes to sort out warnings and use CSS shortcuts.
//    008   25.01.24 Sean Flook                  Changes required after UX review.
//    009   16.02.24 Sean Flook         ESU16_GP If changing page etc ensure the information and selection controls are cleared.
//    010   20.02.24 Sean Flook         ESU16_GP Undone above change as not required.
//    011   23.02.24 Joel Benford      IMANN-287 Correct hover blue
//    012   11.03.24 Sean Flook            GLB12 Adjusted height to remove gap.
//    013   18.03.24 Sean Flook            GLB12 Adjusted height to remove overflow.
//    014   18.03.24 Sean Flook       STRFRM3_OS Set the styling for the header row of the data grid.
//    015   22.03.24 Sean Flook            GLB12 Changed to use dataFormStyle so height can be correctly set.
//    016   29.04.24 Joshua McCormick  IMANN-386 Toolbar changes no title no wrapping with width restrictions
//    017   05.06.24 Sean Flook        IMANN-523 Use the provenance colour for the avatar.
//    018   20.06.24 Sean Flook        IMANN-636 Use the new user rights.
//endregion Version 1.0.0.0
//region Version 1.0.1.0
//    019   31.10.24 Sean Flook       IMANN-1012 Changed height of skeleton control.
//endregion Version 1.0.1.0
//region Version 1.0.5.0
//    020   27.01.25 Sean Flook       IMANN-1077 Upgraded MUI to v6.
//    021   14.03.25 Sean Flook        IMANN-963 Prevent the selection control from displaying if the user cannot edit the data.
//    022   23.04.25 Sean Flook       IMANN-1768 Changes required for selecting properties in property and street forms.
//    023   24.04.25 Sean Flook       IMANN-1760 Prevent multiple renders from occurring.
//endregion Version 1.0.5.0
//region Version 1.0.6.0
//    024   04.07.25 Sean Flook                  Set the new variant parameter for the ADSSelectionControl.
//    025   08.08.25 Sean Flook       IMANN-1960 Moved styling for DataGrid column headers to App.css.
//endregion Version 1.0.6.0
//region Version 1.0.7.0
//    026   13.06.25 Sean Flook       IMANN-1812 Moved controls from @mui/system to @mui/material.
//    027   22.08.25 Sean Flook       IMANN-1812 Corrected type in call to onHighlightListItem.
//    028   28.08.25 Sean Flook       IMANN-1812 Changes required to update MUI to v7.
//    029   28.08.25 Sean Flook       IMANN-1812 Changes required to update MUI X to v8.
//    030   27.10.25 Sean Flook       IMANN-2110 Set getRowHeight to Auto in the grid so that text wraps correctly.
//    031   28.10.25 Sean Flook       IMANN-2110 Use formatTableStartDate and formatTableEndDate for date columns.
//    032   05.12.25 Sean Flook       IMANN-1671 Various fixes for handling polygons.
//    033   17.12.25 Sean Flook       IMANN-2196 Removed the information and selection components as no longer required in this control.
//endregion Version 1.0.7.0
//region Version 1.0.7.2
//    034   01.05.26 Sean Flook       IMANN-2430 Use currentProvenanceSelection when using the list of ids currently selected when deleting.
//endregion Version 1.0.7.2
//
//--------------------------------------------------------------------------------------------------
//endregion header

import React, { useState, useRef, useContext, useEffect, Fragment } from "react";
import PropTypes from "prop-types";

import MapContext from "../context/mapContext";
import UserContext from "../context/userContext";
import PropertyContext from "../context/propertyContext";
import SettingsContext from "../context/settingsContext";
import SelectionContext from "../context/selectionContext";

import { formatTableEndDate, formatTableStartDate, GetLookupLabel } from "../utils/HelperUtils";

import {
  Tooltip,
  IconButton,
  Typography,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Skeleton,
  Avatar,
  Box,
  Stack,
} from "@mui/material";
import ADSActionButton from "../components/ADSActionButton";

import ConfirmDeleteDialog from "../dialogs/ConfirmDeleteDialog";

import BLPUProvenance from "../data/BLPUProvenance";

import { AddCircleOutlineOutlined as AddCircleIcon } from "@mui/icons-material";

import { adsBlueA, adsPaleBlueA } from "../utils/ADSColours";
import { toolbarStyle, ActionIconStyle, dataFormStyle, tooltipStyle, StyledDataGrid } from "../utils/ADSStyles";
import { useTheme } from "@mui/material/styles";

PropertyBLPUProvenanceListTab.propTypes = {
  data: PropTypes.array,
  errors: PropTypes.array,
  loading: PropTypes.bool.isRequired,
  onProvenanceSelected: PropTypes.func.isRequired,
  onProvenanceDelete: PropTypes.func.isRequired,
  onMultiProvenanceDelete: PropTypes.func.isRequired,
};

function PropertyBLPUProvenanceListTab({
  data,
  errors,
  loading,
  onProvenanceSelected,
  onProvenanceDelete,
  onMultiProvenanceDelete,
}) {
  const theme = useTheme();

  const mapContext = useContext(MapContext);
  const userContext = useContext(UserContext);
  const propertyContext = useContext(PropertyContext);
  const settingsContext = useContext(SettingsContext);
  const selectionContext = useContext(SelectionContext);

  const [sortModel, setSortModel] = useState([{ field: "provenanceCode", sort: "asc" }]);
  const [selectionModel, setSelectionModel] = useState({
    type: "include",
    ids: new Set([]),
  });
  const [selectedRow, setSelectedRow] = useState(null);

  const [userCanEdit, setUserCanEdit] = useState(false);

  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);

  const deleteProvenanceId = useRef(null);

  /**
   * Method to display the action buttons for the given row.
   *
   * @param {object} params The grid row parameters.
   * @returns {JSX.Element} The action buttons for the row
   */
  const displayActionButtons = (params) => {
    const onDeleteClick = (event) => {
      event.stopPropagation();
      deleteProvenanceId.current = params.id;
      setOpenDeleteConfirmation(true);
    };

    return (
      selectedRow &&
      params.id === selectedRow && (
        <Stack direction="row" spacing={1} justifyContent="flex-start" alignItems="center">
          <ADSActionButton
            id={`delete-provenance-${params.id}`}
            variant="delete"
            inheritBackground
            disabled={!userCanEdit}
            tooltipTitle="Delete provenance"
            tooltipPlacement="bottom"
            onClick={onDeleteClick}
          />
        </Stack>
      )
    );
  };

  /**
   * Method to get the provenance avatar for the given row.
   *
   * @param {object} params The grid row parameters.
   * @returns {JSX.Element} The provenance avatar for the row.
   */
  function getProvenanceAvatar(params) {
    function getProvenanceText(code) {
      const rec = BLPUProvenance.find((x) => x.id === code);

      if (rec) return rec[GetLookupLabel(settingsContext.isScottish)];
      else return null;
    }

    function getProvenanceColour(code) {
      const rec = BLPUProvenance.find((x) => x.id === code);

      if (rec) return rec.colour;
      else return "#BDBDBD";
    }

    if (params) {
      return (
        <Stack direction="row" spacing={1}>
          <Avatar
            variant="rounded"
            sx={{ height: "24px", width: "24px", backgroundColor: getProvenanceColour(params.row.provenanceCode) }}
          >
            <Typography variant="caption">{params.row.provenanceCode}</Typography>
          </Avatar>
          <Typography display="inline-block" variant="body1">
            {getProvenanceText(params.row.provenanceCode)}
          </Typography>
        </Stack>
      );
    } else return null;
  }

  /**
   * Array of fields (columns) to be displayed in the data grid.
   */
  const provenanceColumns = [
    { field: "id" },
    { field: "uprn" },
    { field: "changeType" },
    { field: "provenanceKey" },
    {
      field: "provenanceCode",
      headerName: "Type",
      display: "flex",
      flex: 30,
      renderCell: getProvenanceAvatar,
    },
    { field: "annotation" },
    { field: "entryDate" },
    {
      field: "startDate",
      headerName: "Start",
      flex: 10,
      type: "date",
      align: "right",
      headerAlign: "right",
      valueGetter: (value, row, column, apiRef) => new Date(value),
      renderCell: formatTableStartDate,
    },
    {
      field: "endDate",
      headerName: "End",
      flex: 10,
      type: "date",
      align: "right",
      headerAlign: "right",
      valueGetter: (value, row, column, apiRef) => new Date(value),
      renderCell: formatTableEndDate,
    },
    { field: "lastUpdateDate" },
    {
      field: "",
      display: "flex",
      flex: 2,
      sortable: false,
      renderCell: displayActionButtons,
    },
  ];

  /**
   * Event to handle when the mouse enters a row in the data grid.
   *
   * @param {object} event The event object.
   */
  const handleRowMouseEnter = (event) => {
    event.preventDefault();

    mapContext.onHighlightListItem("extent", [event.currentTarget.getAttribute("data-id")]);
    setSelectedRow(Number(event.currentTarget.getAttribute("data-id")));
  };

  /**
   * Event to handle when the mouse leaves a row in the data grid.
   */
  const handleRowMouseLeave = () => {
    mapContext.onHighlightClear();
    setSelectedRow(null);
  };

  /**
   * Event to handle when a provenance record is clicked.
   *
   * @param {object} param The grid row parameters.
   * @param {object} event The event object.
   */
  const handleProvenanceClicked = (param, event) => {
    event.stopPropagation();

    if (param && param.field !== "__check__" && param.field !== "") {
      let record = -1;
      for (let i = 0; i < data.length; i++) {
        if (data[i].id === param.id) {
          record = i;
          break;
        }
      }
      propertyContext.onProvenanceDataChange(propertyContext.currentProperty.newProperty ? true : false);
      if (onProvenanceSelected) onProvenanceSelected(param.id, param.row, record, data.length);
    }
  };

  /**
   * Event to handle when the add provenance button is clicked.
   *
   * @param {object} event The event object.
   */
  const handleAddProvenanceClick = (event) => {
    event.stopPropagation();
    propertyContext.onProvenanceDataChange(true);
    if (onProvenanceSelected) onProvenanceSelected(0, null, null, null);
  };

  /**
   * Event to handle when the selection model for the data grid changes.
   *
   * @param {object} selectionModel The current selection model for the data grid.
   */
  const handleSelectionModelChange = (selectionModel) => {
    if (selectionModel.type === "include" && selectionModel.ids.size > 0 && userCanEdit) {
      selectionContext.onSelectionVariantChange("provenance");
      selectionContext.onSelectionCountChange(selectionModel.ids.size);
      selectionContext.onCurrentProvenanceChange([...selectionModel.ids]);
    } else if (selectionModel.type === "exclude" && selectionModel.ids.size === 0 && userCanEdit) {
      selectionContext.onSelectionVariantChange("provenance");
      selectionContext.onSelectionCountChange(data.length);
      const allIds = data.map((x) => x.id);
      selectionContext.onCurrentProvenanceChange(allIds);
    } else if (selectionModel.type === "exclude" && selectionModel.ids.size < data.length && userCanEdit) {
      selectionContext.onSelectionVariantChange("provenance");
      selectionContext.onSelectionCountChange(data.length - selectionModel.ids.size);
      const allIds = data.filter((x) => !selectionModel.ids.has(x.id)).map((x) => x.id);
      selectionContext.onCurrentProvenanceChange(allIds);
    } else {
      selectionContext.onCloseSelection();
    }
  };

  /**
   * Event to handle when the delete confirmation dialog is closed.
   *
   * @param {boolean} deleteConfirmed True if the user has confirmed the delete; otherwise false.
   */
  const handleCloseDeleteConfirmation = (deleteConfirmed) => {
    setOpenDeleteConfirmation(false);
    if (deleteConfirmed) {
      if (selectionContext.currentProvenanceSelection?.length > 0 && onMultiProvenanceDelete) {
        onMultiProvenanceDelete(selectionContext.currentProvenanceSelection);
        selectionContext.onCloseSelection();
        setSelectionModel({
          type: "include",
          ids: new Set([]),
        });
      } else if (deleteProvenanceId.current && onProvenanceDelete) {
        onProvenanceDelete(deleteProvenanceId.current);
        deleteProvenanceId.current = null;
      }
    }
  };

  /**
   * Method to determine if the row is valid or not.
   *
   * @param {number} id The id of the row.
   * @returns {boolean} True if the row is valid; otherwise false.
   */
  const isRowInvalid = (id) => {
    if (errors && errors.length > 0) {
      const rowErrors = errors.filter((x) => x.id === id);
      return rowErrors && rowErrors.length > 0;
    } else return false;
  };

  useEffect(() => {
    if (userCanEdit !== (userContext.currentUser && userContext.currentUser.editProperty))
      setUserCanEdit(userContext.currentUser && userContext.currentUser.editProperty);
  }, [userCanEdit, userContext.currentUser]);

  useEffect(() => {
    if (selectionContext.closeControl) {
      selectionContext.onControlClosed();
      setSelectionModel({
        type: "include",
        ids: new Set([]),
      });
    }
  }, [selectionContext, selectionContext.closeControl]);

  useEffect(() => {
    if (selectionContext.deleteProvenance && selectionContext.currentProvenanceSelection?.length > 0) {
      selectionContext.onDeleteProvenance(false);
      setOpenDeleteConfirmation(true);
    }
  }, [selectionContext]);

  return (
    <Fragment>
      <Box sx={toolbarStyle} id="ads-property-provenance-list-tab">
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle1" sx={{ pl: theme.spacing(2) }} noWrap>
            BLPU provenances
          </Typography>
          <Typography>
            <Tooltip title="Add new provenance record" arrow placement="right" sx={tooltipStyle}>
              <IconButton
                sx={ActionIconStyle()}
                disabled={!userCanEdit}
                onClick={handleAddProvenanceClick}
                size="small"
              >
                <AddCircleIcon />
                <Typography
                  variant="subtitle1"
                  noWrap
                  sx={{
                    pl: theme.spacing(1),
                    pr: theme.spacing(1),
                  }}
                >
                  BLPU provenance
                </Typography>
              </IconButton>
            </Tooltip>
          </Typography>
        </Stack>
      </Box>
      <Box
        sx={{
          ...dataFormStyle("PropertyBLPUProvenanceListTab"),
        }}
      >
        {loading ? (
          <Skeleton variant="rectangular" height="30px" width="100%" />
        ) : data && data.filter((x) => x.changeType !== "D").length > 0 ? (
          <StyledDataGrid
            rows={data.filter((x) => x.changeType !== "D")}
            columns={provenanceColumns}
            initialState={{
              columns: {
                columnVisibilityModel: {
                  id: false,
                  uprn: false,
                  changeType: false,
                  provenanceKey: false,
                  annotation: false,
                  entryDate: false,
                  lastUpdateDate: false,
                },
              },
            }}
            autoPageSize
            checkboxSelection
            disableColumnMenu
            pagination
            sortModel={sortModel}
            rowSelectionModel={selectionModel}
            slotProps={{
              row: {
                onMouseEnter: handleRowMouseEnter,
                onMouseLeave: handleRowMouseLeave,
              },
            }}
            getRowClassName={(params) => `${isRowInvalid(params.id) ? "invalid-row" : "valid-row"}`}
            getRowHeight={(params) => "auto"}
            onCellClick={handleProvenanceClicked}
            onRowSelectionModelChange={(newSelectionModel) => {
              setSelectionModel(newSelectionModel);
              handleSelectionModelChange(newSelectionModel);
            }}
            onSortModelChange={(model) => setSortModel(model)}
          />
        ) : (
          <List
            sx={{
              width: "100%",
              backgroundColor: theme.palette.background.paper,
              pt: theme.spacing(0),
            }}
            component="nav"
            key="key_no_records"
          >
            <ListItemButton
              dense
              disabled={!userCanEdit}
              disableGutters
              onClick={handleAddProvenanceClick}
              sx={{
                height: "30px",
                "&:hover": {
                  backgroundColor: adsPaleBlueA,
                  color: adsBlueA,
                },
              }}
            >
              <ListItemText
                primary={
                  <Typography variant="subtitle1" sx={{ pl: "16px" }}>
                    No provenance records present
                  </Typography>
                }
              />
              <ListItemAvatar
                sx={{
                  minWidth: 32,
                }}
              >
                <Tooltip title="Add BLPU provenance record" arrow placement="bottom" sx={tooltipStyle}>
                  <IconButton disabled={!userCanEdit} onClick={handleAddProvenanceClick} size="small">
                    <AddCircleIcon sx={ActionIconStyle()} />
                  </IconButton>
                </Tooltip>
              </ListItemAvatar>
            </ListItemButton>
          </List>
        )}
      </Box>
      <div>
        <ConfirmDeleteDialog
          variant="provenance"
          recordCount={selectionModel && selectionModel.ids.size > 0 ? selectionModel.ids.size : 0}
          open={openDeleteConfirmation}
          onClose={handleCloseDeleteConfirmation}
        />
      </div>
    </Fragment>
  );
}

export default PropertyBLPUProvenanceListTab;
